import PropTypes from 'prop-types';
import { Modal, Tag } from 'antd';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { TableElement } from '../index';
import { ModifiedType } from './utils';

function PermissionGroupsBatchConfirmDialog(props) {
  const { changedData = {}, isLoading = false, onOk, onCancel } = props;
  const { differences } = changedData;
  const { t } = useTranslation(['icp-form-renderer', 'icp-common']);

  const rowData = useMemo(
    () =>
      differences
        .flatMap((diffGroup) => diffGroup.modifiedItems)
        .map((item) => {
          const { group, pbcToken, targetToken, customScope, modifiedType } = item;
          const targetPath = `${pbcToken} > ${targetToken}`;
          const modifiedPermission = item.name + (customScope ? ` (${customScope})` : '');
          return { group, targetPath, modifiedPermission, modifiedType };
        }),
    [differences],
  );

  const columnDefs = useMemo(
    () => [
      {
        colId: 'group',
        field: 'group',
        headerName: t('permission-groups-batch-editor.group'),
        rowGroup: true,
        hide: true,
      },
      {
        colId: 'targetPath',
        field: 'targetPath',
        headerName: t('permission-groups-batch-editor.target-path'),
        flex: 4,
      },
      {
        colId: 'modifiedPermission',
        field: 'modifiedPermission',
        headerName: t('permission', { ns: 'icp-common' }),
        flex: 2,
      },
      {
        colId: 'modifiedType',
        field: 'modifiedType',
        headerName: t('permission-groups-batch-editor.modified-type'),
        flex: 2,
        cellRenderer: (params) => {
          switch (params.value) {
            case ModifiedType.ADD:
              return <Tag color="green">＋</Tag>;
            case ModifiedType.REMOVE:
              return <Tag color="red">ー</Tag>;
            default:
              return null;
          }
        },
      },
    ],
    [t],
  );

  const gridOptions = useMemo(
    () => ({
      autoGroupColumnDef: {
        headerValueGetter: (params) => params.colDef.headerName,
        flex: 3,
        cellRendererParams: {
          suppressCount: true,
          innerRenderer: (params) => {
            const children = params.node.childrenAfterGroup;
            const getCount = (type, sign, color) => {
              const items = children.filter((child) => child.data?.modifiedType === type);
              return items.length > 0 ? <span style={{ color }}>{sign + items.length}</span> : null;
            };
            const addedCount = getCount(ModifiedType.ADD, '+', 'var(--mes-green-4)');
            const removedCount = getCount(ModifiedType.REMOVE, '-', 'var(--mes-red-4)');
            const both = addedCount != null && removedCount != null;
            return (
              <div>
                <strong>{params.value}</strong> [ {addedCount} {both && ', '} {removedCount} ]
              </div>
            );
          },
        },
      },
      rowGroupPanelShow: 'never',
      groupDisplayType: 'multipleColumns',
      groupDefaultExpanded: 0,
      suppressStatusBarTotalRows: true,
      suppressToolbar: true,
      suppressCellFocus: true,
      rowModelType: 'clientSide',
      rowData,
      columnDefs,
      onRowClicked: (event) => {
        const node = event.node;
        if (node.group) {
          node.setExpanded(!node.expanded);
        }
      },
    }),
    [columnDefs, rowData],
  );

  return (
    <Modal
      title={t('permission-groups-batch-editor.title')}
      centered={true}
      open={true}
      confirmLoading={isLoading}
      okText={t('permission-groups-batch-editor.confirm-and-save')}
      onOk={onOk}
      onCancel={onCancel}
      width="60vw"
      styles={{
        body: { height: '60vh' },
      }}
    >
      <div style={{ marginBottom: 8 }}>{t('permission-groups-batch-editor.confirm-message')}</div>
      <TableElement style={{ height: 'calc(100% - 30px)' }} componentProps={gridOptions} />
    </Modal>
  );
}

PermissionGroupsBatchConfirmDialog.propTypes = {
  changedData: PropTypes.shape({}),
  isLoading: PropTypes.bool,
  onOk: PropTypes.func,
  onCancel: PropTypes.func,
};

export default PermissionGroupsBatchConfirmDialog;
